/**
 * UploadCenterBanner + UploadCenterTopBar — visible feedback for the
 * in-memory upload queue (see contexts/UploadCenterContext.js).
 *
 *   <UploadCenterTopBar />   — 2 px progress strip at top:0, z-60.
 *                              Width follows the aggregate progress.
 *
 *   <UploadCenterBanner />   — sticky pill banner just below the top bar
 *                              ("1 publicación subiéndose · 65%") with retry
 *                              + dismiss actions per errored item.
 *
 * Both components read from useUploadCenter(); when no items are active,
 * they render nothing.
 */
import { AnimatePresence, motion } from 'framer-motion';
import {
  Loader2, CheckCircle2, AlertCircle, RotateCw, X, Image as ImageIcon,
  Video, Film,
} from 'lucide-react';
import {
  useUploadCenter,
  aggregateProgress,
  hasActiveUploads,
  UPLOAD_STATES,
  labelForState,
} from '@/contexts/UploadCenterContext';
import { tr } from '@/contexts/LanguageContext';

const KIND_ICON = { post: ImageIcon, reel: Video, story: Film };

/* ── Top thin progress strip ──────────────────────────────────────── */

export const UploadCenterTopBar = () => {
  const { items } = useUploadCenter();
  const active = hasActiveUploads(items);
  const pct = aggregateProgress(items) ?? 0;
  return (
    <AnimatePresence>
      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[60] h-0.5 bg-primary/15 pointer-events-none"
          data-testid="upload-center-topbar"
        >
          <motion.div
            className="h-full bg-primary"
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ ease: 'linear', duration: 0.18 }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ── Sticky pill banner ───────────────────────────────────────────── */

export const UploadCenterBanner = () => {
  const { items, retry, dismiss } = useUploadCenter();
  // Show banner if there's anything in the queue (incl. errors & published).
  // Published items auto-prune after 3.5 s (see provider).
  if (items.length === 0) return null;

  const activeCount = items.filter((i) => (
    i.state === UPLOAD_STATES.PREPARING
    || i.state === UPLOAD_STATES.UPLOADING
    || i.state === UPLOAD_STATES.PROCESSING
  )).length;
  const errorItems = items.filter((i) => i.state === UPLOAD_STATES.ERROR);
  const publishedItems = items.filter((i) => i.state === UPLOAD_STATES.PUBLISHED);
  const aggPct = aggregateProgress(items);

  // Headline label
  let headline;
  let tone = 'active';
  if (activeCount > 0) {
    headline = activeCount === 1
      ? tr("1 publicación subiéndose…")
      : tr("{0} publicaciones subiéndose…", activeCount);
    tone = 'active';
  } else if (errorItems.length > 0) {
    headline = errorItems.length === 1
      ? tr("No pudimos publicar tu contenido")
      : tr("{0} publicaciones fallaron", errorItems.length);
    tone = 'error';
  } else if (publishedItems.length > 0) {
    headline = publishedItems.length === 1
      ? tr("Tu publicación se ha compartido")
      : tr("{0} publicaciones se han compartido", publishedItems.length);
    tone = 'success';
  } else {
    return null;
  }

  return (
    <div
      className="fixed top-2 left-1/2 -translate-x-1/2 z-[55] max-w-[92vw] w-[min(420px,92vw)] pointer-events-none"
      data-testid="upload-center-banner"
    >
      <AnimatePresence initial={false}>
        <motion.div
          key={tone}
          initial={{ y: -24, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -24, opacity: 0, scale: 0.98 }}
          transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          className={`pointer-events-auto rounded-2xl border backdrop-blur-xl shadow-lg overflow-hidden ${
            tone === 'error'
              ? 'bg-destructive/10 border-destructive/40'
              : tone === 'success'
                ? 'bg-emerald-500/10 border-emerald-400/40'
                : 'bg-card/85 border-border'
          }`}
          data-testid={`upload-center-banner-${tone}`}
        >
          <div className="flex items-center gap-2.5 px-3 py-2">
            <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center flex-shrink-0 ${
              tone === 'error'
                ? 'bg-destructive/20 text-destructive'
                : tone === 'success'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-primary/15 text-primary'
            }`}>
              {tone === 'error' ? (
                <AlertCircle className="w-3.5 h-3.5" />
              ) : tone === 'success' ? (
                <CheckCircle2 className="w-3.5 h-3.5" />
              ) : (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              )}
            </span>
            <span className="text-xs font-semibold flex-1 truncate" data-testid="upload-center-headline">
              {headline}
            </span>
            {tone === 'active' && (
              <span className="text-[10px] font-mono text-muted-foreground tabular-nums" data-testid="upload-center-pct">
                {aggPct ?? 0}%
              </span>
            )}
          </div>
          {/* Aggregate progress (only while active) */}
          {tone === 'active' && (
            <div className="h-0.5 bg-secondary/60" data-testid="upload-center-progress-track">
              <motion.div
                className="h-full bg-primary"
                initial={{ width: 0 }}
                animate={{ width: `${aggPct ?? 0}%` }}
                transition={{ ease: 'linear', duration: 0.2 }}
              />
            </div>
          )}
          {/* Per-item rows — only show when there's something off-happy-path */}
          {(errorItems.length > 0 || (activeCount > 1 && items.length > 1)) && (
            <ul className="divide-y divide-border max-h-[40vh] overflow-y-auto" data-testid="upload-center-list">
              {items.map((item) => (
                <UploadCenterRow
                  key={item.id}
                  item={item}
                  onRetry={() => retry(item.id)}
                  onDismiss={() => dismiss(item.id)}
                />
              ))}
            </ul>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

const UploadCenterRow = ({ item, onRetry, onDismiss }) => {
  const Icon = KIND_ICON[item.kind] || ImageIcon;
  const isError = item.state === UPLOAD_STATES.ERROR;
  const isDone = item.state === UPLOAD_STATES.PUBLISHED;
  return (
    <li
      className="flex items-center gap-2 px-3 py-2"
      data-testid={`upload-center-row-${item.id}`}
      data-state={item.state}
    >
      <span className="w-5 h-5 rounded bg-secondary inline-flex items-center justify-center flex-shrink-0 text-muted-foreground">
        <Icon className="w-3 h-3" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold leading-tight truncate">{item.label}</p>
        <p className={`text-[10px] leading-tight ${isError ? 'text-destructive' : 'text-muted-foreground'}`}>
          {isError ? item.error : labelForState(item)}
        </p>
      </div>
      {isError && (
        <button
          type="button"
          onClick={onRetry}
          className="text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20"
          data-testid={`upload-center-retry-${item.id}`}
        >
          <RotateCw className="w-3 h-3" />{tr("Reintentar")}</button>
      )}
      {(isError || isDone) && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={tr("Descartar")}
          className="w-5 h-5 rounded-full hover:bg-secondary inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
          data-testid={`upload-center-dismiss-${item.id}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </li>
  );
};

export default UploadCenterBanner;
